import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';
import { Tab as TabType, useTabs, validatePageNumber, validateSorting } from 'src/client/helpers';
import { Images, PPSGalleries } from '../index';
import GalleriesContent from '../GalleriesContent/GalleriesContent';
import BotdArchive from '../BotdArchive/BotdArchive';
import PotdArchive from '../PotdArchive/PotdArchive';
import { galleriesRoutes, getGallerySortTypes, routes } from 'src/client/utils/router';
import CountTab from '../../CountTabs/CountTab';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';
import urls from 'src/shared/urls';
import CamsSlider from '../../Cams/CamsSlider/CamsSlider';
import { camsSliderSettings } from 'src/client/helpers/constants';
import { CamsTitle } from 'src/client/containers/Cams/CamsSection/CamsTitle';
import { CamsContext } from 'src/client/containers/Cams/CamsContextProvider';
import { OnPageText } from 'src/client/components/OnPageSeo/OnPageText/OnPageText';
import { IconOnlySortAndViewDropdown } from 'src/client/components/dropdowns';
import { SORT_TYPES as SORT_TYPES_GALLERIES } from 'src/shared/constants/galleries';
import { ContentHeader } from 'src/client/components/ui/ContentHeader';
import styled from 'styled-components';
import CountTabs from '../../CountTabs/CountTabs';
import { ConstrainedContainer, MOBILE_LANDSCAPE } from 'src/client/constants/mediaQueries';
import { useWidePage } from 'src/client/helpers/useFeatureFlags';
import { MainPageTitle } from 'src/client/components/layouts/MainPageTitle';

const {
  galleries: {
    routes: { myGalleries: myGalleriesRoute, galleries: galleriesRoute, images: imagesRoute, botd: botdRoute },
  },
} = routes;

const galleryTabs: Array<TabType> = [
  {
    value: 'Galleries',
    to: '/galleries',
    name: 'Galleries',
    headerTitle: 'Photo Galleries',
  },
  {
    value: 'Images',
    to: '/images',
    name: 'Images',
    headerTitle: 'Top Images',
  },
  {
    value: 'PPSGalleries',
    to: '/galleries/my',
    name: 'My Galleries',
    requiresAuth: true,
    requiresPPSGalleries: true,
    headerTitle: 'My Galleries',
  },
  {
    // both potd and botd use the same value
    value: 'Beauty of the Day',
    to: '/photo-archive',
    name: 'BOTD Archive',
    headerTitle: 'Beauty of the Day',
  },
  {
    // both potd and botd use the same value
    value: 'Beauty of the Day',
    to: '/photo-archive',
    name: 'Daily Photo',
    headerTitle: 'Photo of the Day Archive',
  },
];

const sortKeyByTab: { [key: string]: 'galleriesSorting' } = {
  Galleries: 'galleriesSorting',
  Images: 'galleriesSorting',
  'Beauty of the Day': 'galleriesSorting',
};

const sortTypes = {
  Galleries: SORT_TYPES_GALLERIES,
};

export default function Galleries() {
  useEffect(() => {
    document.body.classList.add('show-jsd'); // show jsd plugin
    return () => document.body.classList.remove('show-jsd');
  }, []);

  const history = useHistory();
  const user = useSelector((state) => state.auth.user);
  const site = useSelector((state) => state.site);
  const itemCounts = useSelector((state) => state.itemCounts);
  const config = useSelector((state) => state.app.config);
  const hasUpdates = config?.hasUpdates;
  const cams = useContext(CamsContext);
  const widePage = useWidePage();

  const filteredTabs = useMemo(() => {
    return galleryTabs.filter((tab) => {
      if (tab.name === 'Daily Photo') {
        return config?.galleries?.potd?.active;
      } else if (tab.name === 'BOTD Archive') {
        return config?.galleries?.botd?.active;
      }
      return true;
    });
  }, [config]);

  const [tabs, activeTab] = useTabs('galleries', filteredTabs, galleriesRoutes);
  const showCamsTitle = !config.hasUpdates && cams.loaded;

  const gallerySortTypes = getGallerySortTypes(config);
  const defaultSort = gallerySortTypes[0].id;

  const renderContentHeader = useCallback(
    ({ collapsePadding = false, showViewOptionsInTabs = false } = {}) => {
      if (!activeTab) {
        return null;
      }

      const tabComponents = tabs
        .filter((countTab) => {
          if (countTab.requiresPPSGalleries) {
            return itemCounts && itemCounts.PPSGalleries && itemCounts.PPSGalleries.total > 0;
          }
          return true;
        })
        .map((tab) => <CountTab key={tab.value} as={Link} active={activeTab.value === tab.value} {...tab} />);

      if (showViewOptionsInTabs && activeTab.value === 'Galleries') {
        tabComponents.push(
          <IconOnlySortAndViewDropdown
            key="options"
            sortSettingsKey={sortKeyByTab[activeTab.value]}
            sortTypes={sortTypes[activeTab.value]}
            pageType="default"
            tab={activeTab.value}
          />,
        );
      }

      const tabsNode = <StyledTabs countParams={[...tabs.map((tab) => tab.value)]}>{tabComponents}</StyledTabs>;

      return (
        <StyledContentHeader
          collapsePadding={collapsePadding}
          tabs={tabsNode}
          title={null}
          subTitle={<OnPageText contentName="text1" />}
        />
      );
    },
    [activeTab, tabs, itemCounts],
  );

  return (
    <GalleriesContainerSwitcher>
      <Helmet>
        <meta property="og:url" content={`https://www.${site.domain}/galleries`} />
        <meta property="prerender:ttl" content="86400" />
      </Helmet>
      {!hasUpdates && user && <CamsSlider camTrackingPosition="sliderTop" mobileOnly settings={camsSliderSettings} />}

      <MainPageTitle>{activeTab?.headerTitle}</MainPageTitle>
      {renderContentHeader({ collapsePadding: widePage, showViewOptionsInTabs: widePage })}
      {showCamsTitle ? <CamsTitle camTrackingPosition="sideBar" /> : null}

      <Switch>
        <Route
          exact
          path={myGalleriesRoute.path}
          render={({ match }) => {
            if (
              match.params.sortBy &&
              validateSorting(match.params.sortBy, gallerySortTypes) &&
              validatePageNumber(match.params.page)
            ) {
              return <PPSGalleries siteName={site.name} siteDomain={site.domain} />;
            }
            return <Redirect to={`/galleries/my/${defaultSort}`} />;
          }}
        />
        <Route
          exact
          path={galleriesRoute.path}
          render={({ match }) => {
            if (!match.params.sortBy) {
              return <GalleriesContent />;
            }
            if (
              match.params.sortBy &&
              validateSorting(match.params.sortBy, gallerySortTypes) &&
              validatePageNumber(match.params.page)
            ) {
              return <GalleriesContent />;
            }
            if (match.params.sortBy && validatePageNumber(match.params.sortBy)) {
              return <GalleriesContent />;
            }
            return <Redirect to="/galleries" />;
          }}
        />
        <Route exact path={imagesRoute.path} component={Images} />
        <Route
          exact
          path={botdRoute.path}
          render={() => {
            if (config.galleries.botd.active) {
              return <BotdArchive />;
            }

            if (config.galleries.potd.active) {
              return <PotdArchive />;
            }

            history.replace(urls.get.notFound);
            return null;
          }}
        />
        <Redirect to={`/galleries/${defaultSort}`} />
      </Switch>
    </GalleriesContainerSwitcher>
  );
}

const StyledContentHeader = styled(ContentHeader)<{ collapsePadding?: boolean }>`
  padding-top: ${(props) => (props.collapsePadding ? '0' : null)};
`;

const StyledTabs = styled(CountTabs)`
  display: flex;
  padding-right: 5px;
  align-items: center;

  .tab {
    padding: 10px 15px;
    height: 60px;
    display: grid;
    place-items: center;
    @media ${MOBILE_LANDSCAPE} {
      height: 100%;
      display: inline;
    }
  }
  .tab span {
    display: block;
    @media ${MOBILE_LANDSCAPE} {
      display: inline;
    }
  }
`;

// @TODO
// Remove after all pages are converted to wide mode.
const GalleriesContainerSwitcher: React.FC = ({ children }) => {
  const widePage = useWidePage();
  if (widePage) {
    return (
      <GalleriesWrapper>
        <ConstrainedContainer extended>{children}</ConstrainedContainer>
      </GalleriesWrapper>
    );
  }

  return <div className="content container">{children}</div>;
};

const GalleriesWrapper = styled.div`
  overflow: hidden;
  background-color: ${(props) => props.theme.primary1};
  margin-top: 10px;
`;
